import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft, CalendarPlus, CheckCircle2, Copy, CopyPlus, Loader2, Plus, Receipt, Save, Send, Sparkles, Trash2, XCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useOrgPlanLimits } from '@/hooks/useOrgPlanLimits';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import {
  useConvertQuoteToInvoice,
  useDeleteQuote,
  useDuplicateQuote,
  useMarkQuoteSent,
  useQuoteDetail,
  useQuoteLinkBuilder,
  useQuotePrefillFromEnquiry,
  useQuotePriceList,
  useQuoteVatSettings,
  useSaveQuote,
  useSendQuote,
  useSetQuoteStatus,
  type QuoteDetail,
} from '@/hooks/useQuotes';
import {
  bookFromQuoteUrl,
  computeQuoteTotals,
  defaultValidUntil,
  effectiveQuoteStatus,
  formatQuoteMoney,
  formatVatRate,
  isQuotePastValidity,
  lineTotal,
  vatRateForOrg,
} from '@/lib/quotes';
import { draftToQuoteForm, isBlankQuoteLines, type AiQuoteDraft } from '@/lib/ai-quote-draft';
import { CURRENCIES, type CurrencyCode } from '@/lib/currency';
import { AiDraftQuoteDialog, type ApplyDraftMode } from '@/components/admin/quotes/AiDraftQuoteDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AdminPage, Panel, StatusChip, adminButton, useAdminPageTitle } from '@/components/admin/ui';
import { cn } from '@/lib/utils';

const NONE = 'none';

const lineSchema = z.object({
  description: z.string().trim().min(1, 'Add a description'),
  service_id: z.string(),
  quantity: z.coerce.number({ invalid_type_error: 'Required' }).positive('Must be more than 0'),
  unit_amount: z.coerce.number({ invalid_type_error: 'Required' }).min(0, 'Cannot be negative'),
  is_optional: z.boolean(),
  /** Price came from the AI proposal writer, not the price list. Not saved. */
  ai_estimate: z.boolean().optional(),
});

const quoteSchema = z.object({
  client_name: z.string().trim().min(1, "Add the customer's name").max(120),
  client_email: z.union([z.literal(''), z.string().trim().email('Enter a valid email')]),
  client_phone: z.string().trim().max(40),
  client_address: z.string().trim().max(300),
  frequency: z.string().trim().max(60),
  notes: z.string().max(2000),
  valid_until: z.string(),
  currency_code: z.enum(['GBP', 'EUR', 'USD']),
  vat_enabled: z.boolean(),
  vat_rate: z.coerce.number({ invalid_type_error: 'Enter a rate' }).min(0).max(100),
  lines: z.array(lineSchema).min(1, 'Add at least one line'),
});

type QuoteFormValues = z.infer<typeof quoteSchema>;

const blankLine = (overrides: Partial<QuoteFormValues['lines'][number]> = {}) => ({
  description: '',
  service_id: NONE,
  quantity: 1,
  unit_amount: 0,
  is_optional: false,
  ...overrides,
});

const fromDetail = (q: QuoteDetail): QuoteFormValues => ({
  client_name: q.client_name,
  client_email: q.client_email ?? '',
  client_phone: q.client_phone ?? '',
  client_address: q.client_address ?? '',
  frequency: q.frequency ?? '',
  notes: q.notes ?? '',
  valid_until: q.valid_until ?? '',
  currency_code: q.currency_code,
  vat_enabled: q.vat_rate > 0,
  vat_rate: q.vat_rate > 0 ? q.vat_rate : 20,
  lines: q.lines.length
    ? q.lines.map((l) => ({
        description: l.description,
        service_id: l.service_id ?? NONE,
        quantity: l.quantity,
        unit_amount: l.unit_amount,
        is_optional: l.is_optional,
      }))
    : [blankLine()],
});

const prettyDate = (value: string | null | undefined) => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'd MMM yyyy');
  } catch {
    return value;
  }
};

const QuoteEditorAdmin = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const isNew = !quoteId || quoteId === 'new';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();

  const enquiryId = isNew ? searchParams.get('enquiry') : null;
  const { data: detail, isLoading: detailLoading } = useQuoteDetail(isNew ? null : quoteId);
  const { data: priceList = [] } = useQuotePriceList(orgId);
  const { data: vatSettings, isLoading: vatLoading } = useQuoteVatSettings(orgId);
  const { data: enquiryPrefill, isLoading: prefillLoading } = useQuotePrefillFromEnquiry(orgId, enquiryId);
  const buildLink = useQuoteLinkBuilder(orgId);

  useAdminPageTitle(isNew ? 'New quote' : detail?.quote_number ?? 'Quote');

  const saveQuote = useSaveQuote();
  const sendQuote = useSendQuote();
  const markSent = useMarkQuoteSent();
  const duplicateQuote = useDuplicateQuote();
  const setStatus = useSetQuoteStatus();
  const deleteQuote = useDeleteQuote();
  const convertToInvoice = useConvertQuoteToInvoice();
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [initialisedFor, setInitialisedFor] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const { data: planLimits, isLoading: planLoading } = useOrgPlanLimits(orgId);
  // Unreadable limits (null) leave it to the function, which re-checks the plan.
  const aiAllowed = planLoading ? undefined : planLimits ? planLimits.aiProposalWriter : true;

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      client_name: '',
      client_email: '',
      client_phone: '',
      client_address: '',
      frequency: '',
      notes: '',
      valid_until: defaultValidUntil(),
      currency_code: 'GBP',
      vat_enabled: false,
      vat_rate: 20,
      lines: [blankLine()],
    },
  });
  const { fields, append, remove, replace } = useFieldArray({ control: form.control, name: 'lines' });

  // Initialise the form once per quote / prefill source.
  const initKey = isNew ? `new:${enquiryId ?? ''}:${searchParams.get('customer') ?? ''}:${searchParams.get('name') ?? ''}` : `edit:${quoteId}`;
  const prefillReady = isNew ? !vatLoading && (!enquiryId || !prefillLoading) : Boolean(detail);
  useEffect(() => {
    if (initialisedFor === initKey || !prefillReady) return;
    if (!isNew && detail) {
      form.reset(fromDetail(detail));
      setInitialisedFor(initKey);
      return;
    }
    if (!isNew) return;
    const vatRate = vatRateForOrg(vatSettings);
    const lines = [] as QuoteFormValues['lines'];
    if (enquiryPrefill?.service_id) {
      const service = priceList.find((s) => s.id === enquiryPrefill.service_id);
      if (service) lines.push(blankLine({ description: service.name, service_id: service.id, unit_amount: service.price ?? 0 }));
    }
    form.reset({
      client_name: enquiryPrefill?.client_name ?? searchParams.get('name') ?? '',
      client_email: enquiryPrefill?.client_email ?? searchParams.get('email') ?? '',
      client_phone: enquiryPrefill?.client_phone ?? searchParams.get('phone') ?? '',
      client_address: enquiryPrefill?.client_address ?? searchParams.get('address') ?? '',
      frequency: enquiryPrefill?.frequency ?? '',
      notes: '',
      valid_until: defaultValidUntil(),
      currency_code: vatSettings?.currency_code ?? 'GBP',
      vat_enabled: vatRate > 0,
      vat_rate: vatRate > 0 ? vatRate : vatSettings?.vat_rate ?? 20,
      lines: lines.length ? lines : [blankLine()],
    });
    setInitialisedFor(initKey);
  }, [initKey, initialisedFor, prefillReady, isNew, detail, enquiryPrefill, vatSettings, priceList, form, searchParams]);

  // If the price list arrives after the enquiry prefill, fill in the requested service's price.
  useEffect(() => {
    if (!isNew || !enquiryPrefill?.service_id || priceList.length === 0) return;
    const lines = form.getValues('lines');
    if (lines.length === 1 && !lines[0].description) {
      const service = priceList.find((s) => s.id === enquiryPrefill.service_id);
      if (service) form.setValue('lines.0', blankLine({ description: service.name, service_id: service.id, unit_amount: service.price ?? 0 }));
    }
  }, [isNew, enquiryPrefill, priceList, form]);

  const watchedLines = form.watch('lines');
  const vatEnabled = form.watch('vat_enabled');
  const vatRate = form.watch('vat_rate');
  const currency = form.watch('currency_code') as CurrencyCode;
  // Computed every render: RHF's watched array can be mutated in place, so a
  // memo keyed on it would go stale.
  const totals = computeQuoteTotals(watchedLines ?? [], vatEnabled ? vatRate : 0);

  const status = detail ? effectiveQuoteStatus(detail.status, detail.valid_until) : 'draft';
  const locked = Boolean(detail && (detail.status === 'accepted' || detail.status === 'declined'));
  const busy = saveQuote.isPending || sendQuote.isPending || markSent.isPending;

  const persist = async (values: QuoteFormValues): Promise<string | null> => {
    if (!assertCanWrite() || !orgId) return null;
    const reopen =
      detail && status === 'expired' && values.valid_until && !isQuotePastValidity(values.valid_until)
        ? ('sent' as const)
        : undefined;
    const { id } = await saveQuote.mutateAsync({
      id: isNew ? null : quoteId,
      organization_id: orgId,
      enquiry_id: detail?.enquiry_id ?? enquiryPrefill?.enquiry_id ?? null,
      customer_id: detail?.customer_id ?? enquiryPrefill?.customer_id ?? searchParams.get('customer') ?? null,
      client_name: values.client_name,
      client_email: values.client_email || null,
      client_phone: values.client_phone || null,
      client_address: values.client_address || null,
      frequency: values.frequency || null,
      notes: values.notes || null,
      valid_until: values.valid_until || null,
      currency_code: values.currency_code,
      vat_rate: values.vat_enabled ? values.vat_rate : 0,
      status: reopen,
      lines: values.lines.map((l) => ({
        description: l.description,
        service_id: l.service_id === NONE ? null : l.service_id,
        quantity: l.quantity,
        unit_amount: l.unit_amount,
        is_optional: l.is_optional,
      })),
    });
    form.reset(values);
    if (isNew) navigate(`/admin/quotes/${id}`, { replace: true });
    return id;
  };

  const onSave = form.handleSubmit(async (values) => {
    try {
      await persist(values);
      toast.success(isNew ? 'Quote created' : 'Quote saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the quote');
    }
  });

  /** Save pending edits (if any) before an action that relies on the stored quote. */
  const saveIfNeeded = (then: (id: string, values: QuoteFormValues) => Promise<void>) =>
    form.handleSubmit(async (values) => {
      try {
        const id = isNew || form.formState.isDirty ? await persist(values) : quoteId!;
        if (id) await then(id, values);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Something went wrong');
      }
    });

  const onSend = saveIfNeeded(async (id, values) => {
    if (!values.client_email) {
      toast.error('Add an email address to send the quote, or copy the link instead.');
      return;
    }
    const result = await sendQuote.mutateAsync({ quoteId: id, organizationId: orgId! });
    if (result.skipped) toast.info('No email address on this quote — nothing was sent.');
    else toast.success(`Quote sent to ${result.recipient ?? values.client_email}`);
  });

  const onCopyLink = saveIfNeeded(async (id) => {
    const token = detail?.public_token;
    if (!token) {
      toast.info('Quote saved — copy the link again in a moment.');
      return;
    }
    if (detail?.status === 'draft') {
      await markSent.mutateAsync({ quoteId: id, organizationId: orgId!, enquiryId: detail.enquiry_id });
    }
    await navigator.clipboard.writeText(buildLink(token));
    toast.success(detail?.status === 'draft' ? 'Link copied — quote marked as sent' : 'Link copied');
  });

  const onDuplicate = async () => {
    if (!detail || !assertCanWrite()) return;
    try {
      const { id } = await duplicateQuote.mutateAsync({ quote: detail, validUntil: defaultValidUntil() });
      toast.success('Copy created as a new draft');
      navigate(`/admin/quotes/${id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not duplicate the quote');
    }
  };

  const onDecline = async () => {
    if (!detail || !orgId || !assertCanWrite()) return;
    try {
      await setStatus.mutateAsync({ quoteId: detail.id, organizationId: orgId, status: 'declined' });
      toast.success('Quote marked as declined');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update the quote');
    } finally {
      setConfirmDecline(false);
    }
  };

  const onDelete = async () => {
    if (!detail || !orgId || !assertCanWrite()) return;
    try {
      await deleteQuote.mutateAsync({ quoteId: detail.id, organizationId: orgId });
      toast.success('Draft deleted');
      navigate('/admin/quotes');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete the quote');
    }
  };

  const onConvertToInvoice = async () => {
    if (!detail || !assertCanWrite()) return;
    try {
      const { invoiceId } = await convertToInvoice.mutateAsync(detail);
      toast.success('Draft invoice created');
      navigate(`/admin/invoices?invoice=${invoiceId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create the invoice');
    }
  };

  const addFromPriceList = (serviceId: string) => {
    const service = priceList.find((s) => s.id === serviceId);
    if (!service) return;
    const lines = form.getValues('lines');
    const replaceBlank = lines.length === 1 && !lines[0].description && !Number(lines[0].unit_amount);
    const line = blankLine({ description: service.name, service_id: service.id, unit_amount: service.price ?? 0 });
    if (replaceBlank) form.setValue('lines.0', line, { shouldDirty: true });
    else append(line);
  };

  /** Fill the editor from an AI draft. Nothing is saved until the owner saves or sends. */
  const applyAiDraft = (draft: AiQuoteDraft, mode: ApplyDraftMode) => {
    const mapped = draftToQuoteForm(draft, {
      existingNotes: form.getValues('notes'),
      knownServiceIds: new Set(priceList.map((s) => s.id)),
    });
    const current = form.getValues('lines');
    const lines = mode === 'append' && !isBlankQuoteLines(current) ? [...current, ...mapped.lines] : mapped.lines;
    replace(lines);
    form.setValue('notes', mapped.notes, { shouldDirty: true });
    form.setValue('valid_until', mapped.valid_until, { shouldDirty: true });
    toast.success('Draft added. Check it over, then save or send.');
  };

  if (!isNew && detailLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isNew && !detail) {
    return (
      <AdminPage>
        <Panel>
          <p className="text-[13.5px] text-muted-foreground">We couldn't find that quote.</p>
          <Button asChild size="sm" variant="ghost" className={cn(adminButton.outline, 'mt-3')}>
            <Link to="/admin/quotes">Back to quotes</Link>
          </Button>
        </Panel>
      </AdminPage>
    );
  }

  const disabled = !canWrite || locked;

  return (
    <AdminPage>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <Button asChild variant="ghost" size="sm" className={adminButton.iconGhost} aria-label="Back to quotes">
            <Link to="/admin/quotes">
              <ArrowLeft />
            </Link>
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {/* The top bar already renders the number as the page h1. */}
              <p className="adm-num truncate text-[18px] font-semibold text-foreground">
                {detail?.quote_number ?? 'New quote'}
              </p>
              <StatusChip status={status} data-testid="quote-status" />
            </div>
            {detail?.sent_at && (
              <p className="text-[12px] text-muted-foreground">Sent {prettyDate(detail.sent_at)}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {detail && status === 'accepted' && (
            <>
              {detail.job_id ? (
                <Button asChild size="sm" variant="ghost" className={adminButton.outline}>
                  <Link to={`/admin/schedule?job=${detail.job_id}`}>
                    <CalendarPlus />
                    View booking
                  </Link>
                </Button>
              ) : (
                <Button
                  size="sm"
                  className={adminButton.primary}
                  disabled={!canWrite}
                  onClick={() =>
                    navigate(
                      bookFromQuoteUrl({
                        ...detail,
                        service_id: detail.lines.find((l) => l.service_id)?.service_id ?? null,
                      }),
                    )
                  }
                >
                  <CalendarPlus />
                  Book this job
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className={adminButton.outline}
                disabled={!canWrite || convertToInvoice.isPending}
                onClick={detail.invoice_id ? () => navigate(`/admin/invoices?invoice=${detail.invoice_id}`) : onConvertToInvoice}
              >
                {convertToInvoice.isPending ? <Loader2 className="animate-spin" /> : <Receipt />}
                {detail.invoice_id ? 'View invoice' : 'Convert to invoice'}
              </Button>
            </>
          )}
          {!locked && (
            <>
              <Button size="sm" variant="ghost" className={adminButton.outline} disabled={!canWrite || busy} onClick={onSave}>
                {saveQuote.isPending ? <Loader2 className="animate-spin" /> : <Save />}
                Save
              </Button>
              {detail && (
                <Button size="sm" variant="ghost" className={adminButton.outline} disabled={!canWrite || busy} onClick={onCopyLink}>
                  <Copy />
                  Copy link
                </Button>
              )}
              <Button size="sm" className={adminButton.primary} disabled={!canWrite || busy} onClick={onSend}>
                {sendQuote.isPending ? <Loader2 className="animate-spin" /> : <Send />}
                {detail && detail.status !== 'draft' ? 'Resend' : 'Send quote'}
              </Button>
            </>
          )}
          {detail && (
            <Button size="sm" variant="ghost" className={adminButton.outline} disabled={!canWrite || duplicateQuote.isPending} onClick={onDuplicate}>
              <CopyPlus />
              Duplicate
            </Button>
          )}
          {detail && !locked && detail.status !== 'draft' && (
            <Button size="sm" variant="ghost" className={adminButton.danger} disabled={!canWrite} onClick={() => setConfirmDecline(true)}>
              <XCircle />
              Mark declined
            </Button>
          )}
          {detail?.status === 'draft' && (
            <Button size="sm" variant="ghost" className={adminButton.danger} disabled={!canWrite} onClick={() => setConfirmDelete(true)}>
              <Trash2 />
              Delete
            </Button>
          )}
        </div>
      </div>

      {detail?.status === 'accepted' && (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/50 p-4 text-[13.5px]" role="status">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <p className="font-semibold text-foreground">
              Accepted by {detail.accepted_name || detail.client_name} on {prettyDate(detail.accepted_at)}
            </p>
            <p className="text-muted-foreground">
              {detail.job_id ? 'This job is booked in.' : 'Book the job in and it will appear on your calendar.'}
            </p>
          </div>
        </div>
      )}
      {detail?.status === 'declined' && (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-4 text-[13.5px]" role="status">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <p className="font-semibold text-foreground">Declined {prettyDate(detail.declined_at)}</p>
            {detail.decline_reason && <p className="text-muted-foreground">“{detail.decline_reason}”</p>}
            <p className="text-muted-foreground">Duplicate it to send a revised quote.</p>
          </div>
        </div>
      )}
      {status === 'expired' && (
        <p className="rounded-xl border border-border bg-muted/50 p-3 text-[13px] text-muted-foreground">
          This quote has passed its valid-until date. Change the date and save to open it again.
        </p>
      )}

      <Form {...form}>
        <form onSubmit={onSave} className="adm-form grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
          <fieldset disabled={disabled} className="min-w-0 space-y-4">
            <Panel title="Customer">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="client_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="client_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="client_phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input type="tel" autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="frequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How often</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Weekly, one-off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="client_address"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Panel>

            <Panel
              title="Prices"
              description="Pick from your services or type your own lines. Optional extras are offered to the customer."
            >
              <div className="space-y-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Select value="" onValueChange={addFromPriceList} disabled={disabled || priceList.length === 0}>
                    <SelectTrigger className="h-9 w-full sm:w-[260px]" aria-label="Add from your services">
                      <SelectValue placeholder={priceList.length ? 'Add from your services…' : 'No services yet'} />
                    </SelectTrigger>
                    <SelectContent>
                      {priceList.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                          {service.price != null ? ` — ${formatQuoteMoney(service.price, currency)}` : ' — price on quote'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="sm" className={adminButton.outline} onClick={() => append(blankLine())}>
                    <Plus />
                    Add line
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={adminButton.outline}
                    onClick={() => append(blankLine({ is_optional: true }))}
                  >
                    <Sparkles />
                    Add optional extra
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(adminButton.soft, 'sm:ml-auto')}
                    onClick={() => setAiOpen(true)}
                  >
                    <Sparkles />
                    Draft with AI
                    {aiAllowed === false && (
                      <span className="rounded-full bg-card px-1.5 py-px text-[10.5px] font-semibold text-muted-foreground">Business</span>
                    )}
                  </Button>
                </div>

                <div className="hidden grid-cols-12 gap-2 px-3 pt-1 sm:grid" aria-hidden="true">
                  <span className="adm-label col-span-6">Description</span>
                  <span className="adm-label col-span-2">Qty</span>
                  <span className="adm-label col-span-2">Unit price</span>
                  <span className="adm-label col-span-2 text-right">Amount</span>
                </div>

                {fields.map((row, index) => {
                  const line = watchedLines?.[index];
                  return (
                    <div
                      key={row.id}
                      className={cn(
                        'grid grid-cols-12 items-start gap-2 rounded-xl border p-2.5',
                        line?.is_optional ? 'border-dashed border-border bg-card' : 'border-border bg-muted/30',
                      )}
                      data-testid={`quote-line-${index}`}
                    >
                      <div className="col-span-12 sm:col-span-6">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.description`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="What's included" aria-label={`Line ${index + 1} description`} {...field} />
                              </FormControl>
                              {line?.ai_estimate && (
                                <p
                                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:text-amber-200"
                                  data-testid={`quote-line-${index}-ai-estimate`}
                                >
                                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                                  AI estimate: check the price
                                </p>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.quantity`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input type="number" step="0.01" min="0" aria-label={`Line ${index + 1} quantity`} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.unit_amount`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  aria-label={`Line ${index + 1} unit price`}
                                  {...field}
                                  onChange={(event) => {
                                    field.onChange(event);
                                    // Once the owner sets the price themselves it's no longer an estimate.
                                    if (form.getValues(`lines.${index}.ai_estimate`)) form.setValue(`lines.${index}.ai_estimate`, false);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-5 flex items-center justify-end gap-1 pt-1.5 sm:col-span-2">
                        <span className="adm-num text-[13px] font-semibold text-foreground" data-testid={`quote-line-${index}-total`}>
                          {formatQuoteMoney(line ? lineTotal(line) : 0, currency)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={cn(adminButton.iconGhost, 'hover:text-destructive')}
                          aria-label={`Remove line ${index + 1}`}
                          disabled={fields.length === 1}
                          onClick={() => remove(index)}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                      <div className="col-span-12 -mt-0.5 px-0.5">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.is_optional`}
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(v) => field.onChange(Boolean(v))}
                                  aria-label={`Line ${index + 1} is an optional extra`}
                                />
                              </FormControl>
                              <FormLabel className="!mb-0 text-[12px] font-normal text-muted-foreground">
                                Optional extra — the customer can choose to add it
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  );
                })}
                {form.formState.errors.lines?.root && (
                  <p className="text-sm text-destructive">{form.formState.errors.lines.root.message}</p>
                )}
              </div>
            </Panel>

            <Panel title="Notes for the customer">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea rows={4} placeholder="Anything they should know — what's included, access, products used…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Panel>
          </fieldset>

          <fieldset disabled={disabled} className="min-w-0 space-y-4">
            <Panel title="Summary" className="xl:sticky xl:top-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="valid_until"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valid until</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="currency_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CURRENCIES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.code} ({c.symbol})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 p-3">
                  <FormField
                    control={form.control}
                    name="vat_enabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} aria-label="Add VAT" />
                        </FormControl>
                        <FormLabel className="!mb-0">Add VAT</FormLabel>
                      </FormItem>
                    )}
                  />
                  {vatEnabled && (
                    <FormField
                      control={form.control}
                      name="vat_rate"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-1.5 space-y-0">
                          <FormControl>
                            <Input type="number" step="0.01" min="0" max="100" className="h-8 w-20 text-right" aria-label="VAT rate" {...field} />
                          </FormControl>
                          <span className="text-[13px] text-muted-foreground">%</span>
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <dl className="space-y-2 rounded-xl border border-border bg-card p-4 text-[13px]" data-testid="quote-totals">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Subtotal</dt>
                    <dd className="adm-num" data-testid="quote-subtotal">{formatQuoteMoney(totals.subtotal, currency)}</dd>
                  </div>
                  {vatEnabled && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">VAT ({formatVatRate(vatRate)})</dt>
                      <dd className="adm-num" data-testid="quote-vat">{formatQuoteMoney(totals.vat, currency)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-border pt-2 text-[15px] font-semibold">
                    <dt>Total</dt>
                    <dd className="adm-num" data-testid="quote-total">{formatQuoteMoney(totals.total, currency)}</dd>
                  </div>
                </dl>
                {totals.optionalAvailable > 0 && (
                  <p className="text-[12px] text-muted-foreground" data-testid="quote-optional-total">
                    Plus up to {formatQuoteMoney(totals.optionalAvailable, currency)} in optional extras
                    {vatEnabled ? ' (before VAT)' : ''}.
                  </p>
                )}
                {!vatSettings?.vat_registered && !vatEnabled && (
                  <p className="text-[12px] text-muted-foreground">
                    Prices are shown as entered. VAT registered? Turn it on in VAT settings on the Quotes page.
                  </p>
                )}
              </div>
            </Panel>
          </fieldset>
        </form>
      </Form>

      <AiDraftQuoteDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        orgId={orgId}
        allowed={aiAllowed}
        customerName={form.watch('client_name')}
        frequency={form.watch('frequency')}
        currency={currency}
        hasExistingLines={!isBlankQuoteLines(watchedLines ?? [])}
        onApply={applyAiDraft}
      />

      <AlertDialog open={confirmDecline} onOpenChange={setConfirmDecline}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this quote as declined?</AlertDialogTitle>
            <AlertDialogDescription>
              The customer won't be able to accept it any more. You can duplicate it later to send a new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDecline}>Mark declined</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
};

export default QuoteEditorAdmin;
